import React, { FC } from 'react';
import { Space, Typography, Row, Col, Image } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Coords } from 'google-map-react';
import { isEmpty } from 'lodash';
import { WarningTwoTone } from '@ant-design/icons';

import {
  CountryField,
  StatusField,
  TextField,
  GoogleMap,
  MapMarker,
  Avatar,
  Drawer,
} from '~/components';
import { useMCC } from '~/hooks';
import { locations } from '~/routes';
import { Location } from '~/types';
import LocationEdit from './LocationEdit';
import LocationDelete from './LocationDelete';
import { usePageShow } from '~/hooks/usePageQuery';
import useLoadImage from '~/hooks/useLoadImage';
import { LocationIcon } from '~/img';
import theme from '~/theme';

import WorkingHoursField from './WorkingHoursField';

const LocationView: FC<any> = (props: { location?: Location }) => {
  const { location } = props;

  const geolocation = {
    lat: location?.address?.geolocation?.latitude,
    lng: location?.address?.geolocation?.longitude,
  };

  const locationCountry = location?.address?.country;

  if (geolocation.lat && geolocation.lng) {
    return (
      <Row gutter={16}>
        <Col span={24} className="LocationShow-Map">
          <GoogleMap
            center={geolocation as Coords}
            zoom={14}
            options={{ fullscreenControl: false }}
          >
            <MapMarker lat={geolocation.lat} lng={geolocation.lng} />
          </GoogleMap>
        </Col>
      </Row>
    );
  }
  return (
    <Row gutter={16}>
      <Col span={24} className="LocationShow-Map">
        <GoogleMap options={{ fullscreenControl: false }} defaultCountry={locationCountry} />
      </Col>
    </Row>
  );
};

const LocationShow: FC<any> = (props) => {
  const { className } = props;
  const { data, open, showClose } = usePageShow<Location>(locations);

  const [t] = useTranslation('locations');
  const dt = (key: string): string => t(`translation:${key}`);
  const { getMCC } = useMCC();

  return (
    <Drawer
      className={className}
      title={
        <Space>
          <Avatar icon={<LocationIcon />} file={data?.branding?.icon as any} />
          <div>{data?.name}</div>
        </Space>
      }
      onClose={showClose}
      visible={open}
      footer={
        <div
          style={{
            textAlign: 'right',
          }}
        >
          <LocationDelete id={data?.id as string} closeParent={showClose} />
          <LocationEdit initialValues={data as Location} />
        </div>
      }
    >
      <Space direction="vertical" size="large" style={fullWidth}>
        <Space direction="vertical" size="middle" style={fullWidth}>
          <StatusField data={data?.status} />
          <TextField label={t('merchantCategoryCode')}>
            {getMCC(data?.merchantCategoryCode)}
          </TextField>
        </Space>
        <Space direction="vertical" size="middle" style={fullWidth}>
          <Typography.Title level={5} className="LocationShow-Title">
            {dt('address')}
          </Typography.Title>
          <TextField label={dt('country')}>
            <CountryField country={data?.address?.country} />
          </TextField>
          <TextField label={dt('postalCode')}>{data?.address?.postalCode}</TextField>
          <TextField label={dt('region')}>{data?.address?.region}</TextField>
          <Row gutter={16}>
            <Col span={12}>
              <TextField label={dt('city')}>{data?.address?.city}</TextField>
            </Col>
            <Col span={12}>
              <TextField label={dt('cityLocalized')}>{data?.address?.cityLocalized}</TextField>
            </Col>
          </Row>
          <TextField label={dt('addressLine')}>{data?.address?.addressLine}</TextField>
        </Space>
        <Space direction="vertical" size="middle" style={fullWidth}>
          <Typography.Title level={5} className="LocationShow-Title">
            {t('geolocation')}
          </Typography.Title>
          <Row gutter={16}>
            <Col span={12}>
              <TextField label={t('latitude')}>{data?.address?.geolocation?.latitude}</TextField>
            </Col>
            <Col span={12}>
              <TextField label={t('longitude')}>{data?.address?.geolocation?.longitude}</TextField>
            </Col>
          </Row>
          <LocationView location={data} />
          {data?.address?.note && (
            <TextField label={t('Extra notes to address')}>{data?.address?.note}</TextField>
          )}
        </Space>
        {!!data?.branding?.photos?.length && (
          <Space direction="vertical" size="middle" style={fullWidth}>
            <Typography.Title level={5} className="LocationShow-Title">
              {t('Photos')}
            </Typography.Title>
            <Row gutter={[16, 16]}>
              {data.branding.photos.map((item) => {
                return (
                  <Col span={12} key={item.id}>
                    <PhotoField file={item} />
                  </Col>
                );
              })}
            </Row>
          </Space>
        )}
        {!isEmpty(data?.schedule) && (
          <Space direction="vertical" size="middle" style={fullWidth}>
            <Typography.Title level={5} className="LocationShow-Title">
              {t('Working hours')}
            </Typography.Title>
            <WorkingHoursField record={data?.schedule} />
          </Space>
        )}
      </Space>
    </Drawer>
  );
};

const PhotoField: FC<any> = (props) => {
  const { file, loading } = props;
  const imageLoad = useLoadImage(file);
  const resultLoading = imageLoad.loading || loading;
  const resultSrc = imageLoad.dataURL;

  return (
    <ImageWrapper>
      {!imageLoad.error ? (
        <Image src={resultSrc} loading={resultLoading} />
      ) : (
        <Space className="PhotoField-Space" align="center" direction="vertical">
          <WarningTwoTone className="PhotoField-WarningIcon" twoToneColor={theme.warningColor} />
          <Typography.Text className="PhotoField-ErrorText">{imageLoad.error}</Typography.Text>
        </Space>
      )}
    </ImageWrapper>
  );
};

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1;
  border: 1px solid #f0f0f0;
  border-radius: ${theme.borderRadiusBase}px;
  .PhotoField-Space {
    padding: 8px;
    .PhotoField-WarningIcon svg {
      font-size: 32px;
    }
    .PhotoField-ErrorText {
      color: ${theme.warningColor};
    }
  }
`;

const fullWidth = { width: '100%' };

export default styled(LocationShow)`
  .LocationShow-Title {
    margin-bottom: 0;
  }
  .LocationShow-Map {
    width: 100%;
    height: 200px;
  }
  .ant-drawer-wrapper-body .ant-drawer-header {
    padding-top: 18px;
  }
`;
